import { useContext, useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from "carbon-react/esm/components/flat-table";
import Loader from "carbon-react/esm/components/loader/__next__";
import Message from "carbon-react/esm/components/message";
import {
  Option,
  SearchableSelect as Select,
} from "../components/SearchableSelect";
import Textbox from "carbon-react/esm/components/textbox";
import { calculateExpectedPayment } from "@loan-management/calculations";
import {
  loanAccountPurposesForEventTypes,
  type LoanAccountPurpose,
} from "@loan-management/contracts";

import { ApplicationApiError } from "../api/applicationApi";
import { accountPurposes } from "../api/accountingSettingsApi";
import {
  getLoanView,
  performLoanAction,
  type LoanDetailResponse,
} from "../api/loanOperationsApi";
import { EmbeddedSessionContext } from "../auth/EmbeddedSessionProvider";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { CurrencyTextbox } from "../components/CurrencyTextbox";
import {
  DateValue,
  FinancialAmount,
  RatePercentage,
} from "../components/FinancialValue";
import { LoanEventTypes } from "../components/LoanEventTypes";
import { selectedEventTypes } from "../components/LoanEventTypes";
import { MetricCard } from "../components/MetricCard";
import { SageJournalLink } from "../components/SageJournalLink";
import { StatusPill } from "../components/StatusPill";
import { usePaginatedRows } from "../components/TablePagination";

export function LoanDetailPage({
  loanId,
  onNavigate,
  view,
}: {
  loanId: string;
  onNavigate(path: string): void;
  view: string;
}) {
  const { api } = useContext(EmbeddedSessionContext);
  const [data, setData] = useState<LoanDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"terms" | "schedule" | null>(null);
  const [working, setWorking] = useState(false);
  const [note, setNote] = useState("");
  const load = () => {
    if (api)
      void getLoanView<LoanDetailResponse>(api, "loan", { loanId })
        .then(setData)
        .catch(() => setError("Loan details could not be loaded."));
  };
  useEffect(load, [api, loanId]);
  if (!data && !error)
    return <Loader loaderLabel="Loading loan" size="medium" />;
  if (!data)
    return (
      <Message open title="Loan could not be loaded" variant="error">
        {error}
      </Message>
    );
  const loan = data.loan;
  const terms = data.terms[0];
  const termId = String(terms?.id ?? "");
  const schedule = selectCurrentSchedule(data.schedules, termId);
  const currency = String(loan.currency_code ?? "USD");
  const expectedPayment = expectedPaymentForDetail(data);
  const action = async (body: Record<string, unknown>) => {
    if (!api) return;
    setWorking(true);
    setError(null);
    try {
      await performLoanAction(api, body);
      setConfirm(null);
      load();
    } catch (actionError) {
      if (actionError instanceof ApplicationApiError) {
        const details = Object.values(actionError.fieldErrors);
        setError(
          `${actionError.message}${details.length ? `: ${details.join("; ")}` : ""} (${actionError.code}; correlation ${actionError.correlationId})`,
        );
      } else
        setError(
          "The action could not be completed. Review validation and accounting settings.",
        );
    } finally {
      setWorking(false);
    }
  };
  return (
    <div className="operational-stack">
      {error ? (
        <Message open title="Action could not be completed" variant="error">
          {error}
        </Message>
      ) : null}
      <nav aria-label="Loan sections" className="loan-subnav">
        {[
          "overview",
          "terms",
          "rates",
          "fees",
          "event-types",
          "accounting",
          "amortization",
          "payments",
          "journal-entries",
          "reconciliation",
          "audit",
        ].map((item) => (
          <a
            aria-current={view === item ? "page" : undefined}
            href={`/loans/${loanId}/${item}`}
            key={item}
            onClick={(event) => {
              event.preventDefault();
              onNavigate(`/loans/${loanId}/${item}`);
            }}
          >
            {item
              .replace("journal-entries", "journal entries")
              .replace("event-types", "event types")}
          </a>
        ))}
      </nav>
      {view === "overview" ? (
        <section aria-label="Loan summary" className="metric-grid">
          <Metric label="Original principal">
            <FinancialAmount
              currency={currency}
              value={String(terms?.original_principal ?? "")}
            />
          </Metric>
          <Metric label="Current principal">
            <FinancialAmount
              currency={currency}
              value={String(
                [...data.periods]
                  .reverse()
                  .find(
                    (period) =>
                      String(period.accrual_date) <=
                      new Date().toISOString().slice(0, 10),
                  )?.ending_principal ??
                  terms?.original_principal ??
                  "",
              )}
            />
          </Metric>
          <Metric label="Current rate">
            <RatePercentage
              value={String(data.rates.at(-1)?.annual_rate ?? "")}
            />
          </Metric>
          <Metric label="Expected payment">
            <FinancialAmount currency={currency} value={expectedPayment} />
          </Metric>
          <Metric label="Maturity">
            <DateValue value={String(terms?.maturity_date ?? "")} />
          </Metric>
          <Metric
            label="Terms"
            tone={
              terms?.status === "active"
                ? "positive"
                : terms?.status === "draft"
                  ? "caution"
                  : "default"
            }
          >
            <StatusPill
              label={String(terms?.status ?? "unavailable")}
              size="M"
              tone={
                terms?.status === "active"
                  ? "positive"
                  : terms?.status === "draft"
                    ? "warning"
                    : "neutral"
              }
            />
          </Metric>
          <Metric
            label="Schedule"
            tone={
              schedule?.status === "active" || schedule?.status === "ready"
                ? "positive"
                : schedule?.status === "validation_failed"
                  ? "negative"
                  : "default"
            }
          >
            <StatusPill
              label={String(schedule?.status ?? "not generated")}
              size="M"
              tone={
                schedule?.status === "active" || schedule?.status === "ready"
                  ? "positive"
                  : schedule?.status === "validation_failed"
                    ? "negative"
                    : "neutral"
              }
            />
          </Metric>
        </section>
      ) : null}
      {view === "overview" ? (
        <Overview data={data} currency={currency} />
      ) : view === "terms" ? (
        <Terms
          currency={currency}
          data={data}
          onActivate={() => setConfirm("terms")}
          onEdit={() => onNavigate(`/loans/${loanId}/edit`)}
        />
      ) : view === "rates" ? (
        <SimpleTable
          caption="Rate segments"
          columns={["effective_date", "annual_rate", "payment_amount"]}
          currency={currency}
          rows={data.rates}
        />
      ) : view === "fees" ? (
        <SimpleTable
          caption="Fees"
          columns={[
            "fee_type",
            "description",
            "amount",
            "treatment",
            "amortization_method",
          ]}
          currency={currency}
          rows={data.fees}
        />
      ) : view === "event-types" ? (
        <LoanEventTypes
          data={data}
          loanId={loanId}
          onAction={action}
          working={working}
        />
      ) : view === "accounting" ? (
        <LoanAccounting
          data={data}
          loanId={loanId}
          onAction={action}
          working={working}
        />
      ) : view === "amortization" ? (
        <Amortization
          canGenerate={terms?.status === "active"}
          data={data}
          currency={currency}
          generate={() =>
            void action({
              action: "generate_schedule",
              loanId,
              termVersionId: termId,
            })
          }
          activate={() => setConfirm("schedule")}
        />
      ) : view === "payments" ? (
        <Payments
          data={data}
          loanId={loanId}
          onAction={action}
          working={working}
        />
      ) : view === "journal-entries" ? (
        <SimpleTable
          caption="Accounting events"
          columns={["event_date", "event_type", "amount", "posting_status"]}
          currency={currency}
          rows={data.events}
        />
      ) : view === "reconciliation" ? (
        <Message open title="Loan reconciliation" variant="info">
          Run and resolve balance comparisons from the Reconciliation section.
        </Message>
      ) : view === "audit" ? (
        <Audit loanId={loanId} />
      ) : (
        <Overview data={data} currency={currency} />
      )}
      <section className="operational-section">
        <h2>Notes</h2>
        <div className="button-row">
          <Textbox
            label="Add a note"
            onChange={(event) => setNote(event.target.value)}
            value={note}
          />
          <Button
            disabled={!note || working}
            onClick={() => {
              void action({ action: "add_note", loanId, note });
              setNote("");
            }}
            variant="default"
            variantType="secondary"
          >
            Add note
          </Button>
        </div>
        {data.notes.map((item) => (
          <p key={item.id}>
            {item.note}
            <br />
            <small className="muted-text">
              {new Date(item.created_at).toLocaleString()}
            </small>
          </p>
        ))}
      </section>
      <ConfirmationDialog
        confirmLabel="Activate"
        isConfirming={working}
        onCancel={() => setConfirm(null)}
        onConfirm={() =>
          void action(
            confirm === "terms"
              ? { action: "activate_terms", termVersionId: String(terms?.id) }
              : {
                  action: "activate_schedule",
                  scheduleId: String(schedule?.id),
                },
          )
        }
        open={confirm !== null}
        title={
          confirm === "terms"
            ? "Activate immutable loan terms?"
            : "Activate immutable amortization schedule?"
        }
      >
        Activation locks this version and supersedes any prior active version.
      </ConfirmationDialog>
    </div>
  );
}
function Overview({
  data,
  currency,
}: {
  data: LoanDetailResponse;
  currency: string;
}) {
  return (
    <div className="operational-stack">
      <section className="operational-section">
        <h2>Collateral</h2>
        <SimpleTable
          caption="Loan collateral"
          columns={[
            "asset_type",
            "description",
            "asset_identifier",
            "estimated_value",
            "valuation_date",
            "vin",
            "sage_fixed_asset_id",
          ]}
          rows={data.assets}
        />
      </section>
      <PrincipalChart periods={data.periods} />
      <section className="operational-section">
        <h2>Contract timeline</h2>
        <div className="timeline">
          {data.periods
            .filter(
              (_, index) => index === 0 || index === data.periods.length - 1,
            )
            .map((period) => (
              <div key={String(period.id)}>
                <span />
                <strong>
                  {Number(period.period_number) === 1
                    ? "First payment"
                    : "Maturity"}
                </strong>
                <DateValue value={String(period.expected_bank_payment_date)} />
                <FinancialAmount
                  currency={currency}
                  value={String(period.ending_principal)}
                />
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}
const standardDimensions = [
  ["entity", "Entity"],
  ["location", "Location"],
  ["department", "Department"],
  ["class", "Class"],
  ["project", "Project"],
  ["vendor", "Vendor"],
  ["customer", "Customer"],
  ["employee", "Employee"],
  ["item", "Item"],
] as const;
function LoanAccounting({
  data,
  loanId,
  onAction,
  working,
}: {
  data: LoanDetailResponse;
  loanId: string;
  onAction(body: Record<string, unknown>): Promise<void>;
  working: boolean;
}) {
  const initial = Object.fromEntries(
    data.accountingMappings.map((mapping) => [
      mapping.mapping_key,
      mapping.sage_master_record_id,
    ]),
  );
  const [draft, setDraft] = useState<Record<string, string>>(initial);
  useEffect(() => setDraft(initial), [data.accountingMappings]);
  const requiredAccountPurposes = new Set<LoanAccountPurpose>(
    loanAccountPurposesForEventTypes(selectedEventTypes(data.terms[0])),
  );
  const dimensions = [
    ...standardDimensions,
    ...[
      ...new Set(
        data.accountingOptions
          .filter(
            (option) =>
              option.record_type === "user_defined" && option.dimension_key,
          )
          .map((option) => option.dimension_key!),
      ),
    ].map((key) => [key, key] as const),
  ];
  const optionsFor = (recordType: string, key?: string) =>
    data.accountingOptions.filter(
      (option) =>
        option.record_type === recordType &&
        (recordType !== "user_defined" || option.dimension_key === key),
    );
  const mapping = (
    key: string,
    label: string,
    options: LoanDetailResponse["accountingOptions"],
  ) => (
    <Select
      id={key.replace(":", "-")}
      key={key}
      label={label}
      onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
      value={draft[key] ?? ""}
    >
      <Option text="Not configured" value="" />
      {options.map((option) => (
        <Option
          key={option.id}
          text={`${option.display_name} (${option.sage_record_no})${option.status === "inactive" ? " - inactive" : ""}`}
          value={option.id}
        />
      ))}
    </Select>
  );
  return (
    <div className="operational-stack">
      <Message open title="Loan-specific accounting" variant="info">
        Only accounts used by enabled event types are shown. Existing mappings
        remain saved if an event type is disabled and later re-enabled.
      </Message>
      <section className="operational-section">
        <h2>Accounts</h2>
        <div className="settings-grid">
          {accountPurposes
            .filter(([purpose]) =>
              requiredAccountPurposes.has(purpose as LoanAccountPurpose),
            )
            .map(([purpose, label]) =>
              mapping(`account:${purpose}`, label, optionsFor("gl_account")),
            )}
        </div>
      </section>
      <section className="operational-section">
        <h2>Dimensions</h2>
        <div className="settings-grid">
          {dimensions.map(([key, label]) =>
            mapping(
              `dimension:${key}`,
              label,
              optionsFor(
                standardDimensions.some(([type]) => type === key)
                  ? key
                  : "user_defined",
                key,
              ),
            ),
          )}
        </div>
      </section>
      <div className="editor-actions">
        <Button
          disabled={working}
          onClick={() =>
            void onAction({
              action: "save_loan_accounting",
              loanId,
              mappings: draft,
            })
          }
          variant="default"
          variantType="primary"
        >
          {working ? "Saving" : "Save loan accounting"}
        </Button>
      </div>
    </div>
  );
}
function Terms({
  currency,
  data,
  onActivate,
  onEdit,
}: {
  currency: string;
  data: LoanDetailResponse;
  onActivate(): void;
  onEdit(): void;
}) {
  const term = data.terms[0];
  return (
    <section className="operational-section">
      <div className="section-heading-row">
        <h2>Term version {String(term?.version_number ?? "")}</h2>
        <div className="button-row">
          <Button onClick={onEdit} variant="default" variantType="secondary">
            Edit loan
          </Button>
          {term?.status === "draft" ? (
            <Button
              onClick={onActivate}
              variant="default"
              variantType="primary"
            >
              Activate terms
            </Button>
          ) : null}
        </div>
      </div>
      <SimpleTable
        caption="Loan terms"
        columns={[
          "original_principal",
          "payment_amount",
          "origination_date",
          "funding_date",
          "first_accrual_date",
          "first_payment_application_date",
          "first_expected_bank_payment_date",
          "maturity_date",
          "payment_frequency",
          "amortization_term_months",
          "balloon_amount",
          "day_count_convention",
          "business_day_convention",
        ]}
        currency={currency}
        rows={term ? [term] : []}
      />
    </section>
  );
}
function Amortization({
  activate,
  canGenerate,
  currency,
  data,
  generate,
}: {
  activate(): void;
  canGenerate: boolean;
  currency: string;
  data: LoanDetailResponse;
  generate(): void;
}) {
  const termId = String(data.terms[0]?.id ?? "");
  const schedule = selectCurrentSchedule(data.schedules, termId);
  const paginatedPeriods = usePaginatedRows(data.periods);
  return (
    <section className="operational-section">
      <div className="section-heading-row">
        <div>
          <h2>Amortization schedule</h2>
          <p>
            {schedule
              ? `Version ${String(schedule.version_number)} · ${String(schedule.engine_version)}`
              : "No schedule generated for these terms"}
          </p>
        </div>
        <div className="button-row">
          <Button
            disabled={!canGenerate}
            onClick={generate}
            variant="default"
            variantType="secondary"
          >
            Generate schedule
          </Button>
          {schedule?.status === "ready" ? (
            <Button onClick={activate} variant="default" variantType="primary">
              Activate schedule
            </Button>
          ) : null}
        </div>
      </div>
      {!canGenerate ? (
        <Message open title="Activate updated terms" variant="info">
          Activate this term version before generating its amortization
          schedule.
        </Message>
      ) : null}
      {(data.scheduleValidation ?? []).map((issue) => (
        <Message
          key={`${issue.code}-${issue.period_number ?? "schedule"}`}
          open
          title={issue.code.replaceAll("_", " ")}
          variant={issue.severity === "error" ? "error" : "warning"}
        >
          {issue.message}
          {issue.period_number ? ` Period ${issue.period_number}.` : ""}
        </Message>
      ))}
      {data.periods.length === 0 ? (
        <p className="muted-text">
          {canGenerate
            ? "Generate a schedule for the active terms."
            : "No schedule is shown because the updated terms are not active."}
        </p>
      ) : (
        <>
          <FlatTable
            caption="Amortization schedule supporting data"
            colorTheme="light"
            overflowX="auto"
          >
            <FlatTableHead>
              <FlatTableRow>
                {[
                  "Period",
                  "Accrual",
                  "Application",
                  "Expected payment date",
                  "Posting status",
                  "GL batch RECORDNO",
                  "Beginning principal",
                  "Rate",
                  "Days",
                  "Expected interest",
                  "Expected principal",
                  "Additional principal",
                  "Fees",
                  "Total payment",
                  "Balloon",
                  "Ending principal",
                ].map((column) => (
                  <FlatTableHeader key={column}>{column}</FlatTableHeader>
                ))}
              </FlatTableRow>
            </FlatTableHead>
            <FlatTableBody>
              {paginatedPeriods.visibleRows.map((period) => (
                <FlatTableRow key={String(period.id)}>
                  <FlatTableCell>{String(period.period_number)}</FlatTableCell>
                  <FlatTableCell>
                    <DateValue value={String(period.accrual_date)} />
                  </FlatTableCell>
                  <FlatTableCell>
                    <DateValue
                      value={String(period.payment_application_date)}
                    />
                  </FlatTableCell>
                  <FlatTableCell>
                    <DateValue
                      value={String(period.expected_bank_payment_date)}
                    />
                  </FlatTableCell>
                  <FlatTableCell>
                    <StatusPill
                      label={postingStateLabel(period.posting_state)}
                      tone={
                        period.posting_state === "posted"
                          ? "positive"
                          : period.posting_state === "partially_posted"
                            ? "warning"
                            : "neutral"
                      }
                    />
                  </FlatTableCell>
                  <FlatTableCell>
                    {period.gl_batches.length > 0 ? (
                      <span className="sage-record-links">
                        {period.gl_batches.map((batch) =>
                          batch.recordUrl ? (
                            <SageJournalLink
                              href={batch.recordUrl}
                              journalId={batch.recordNo}
                              key={batch.recordNo}
                            />
                          ) : (
                            <span key={batch.recordNo}>{batch.recordNo}</span>
                          ),
                        )}
                      </span>
                    ) : (
                      "—"
                    )}
                  </FlatTableCell>
                  {[
                    "beginning_principal",
                    "annual_rate",
                    "day_count",
                    "interest_accrued",
                    "principal_applied",
                    "additional_principal",
                    "fees",
                    "cash_payment",
                    "balloon_amount",
                    "ending_principal",
                  ].map((field) => (
                    <FlatTableCell key={field}>
                      {field === "annual_rate" ? (
                        <RatePercentage value={String(period[field])} />
                      ) : field === "day_count" ? (
                        String(period[field])
                      ) : (
                        <FinancialAmount
                          currency={currency}
                          value={String(period[field])}
                        />
                      )}
                    </FlatTableCell>
                  ))}
                </FlatTableRow>
              ))}
            </FlatTableBody>
          </FlatTable>
          {paginatedPeriods.pagination}
        </>
      )}
    </section>
  );
}

export function postingStateLabel(
  state: LoanDetailResponse["periods"][number]["posting_state"],
): string {
  return state === "partially_posted"
    ? "Partially posted"
    : state === "posted"
      ? "Posted"
      : "Scheduled";
}

function Payments({
  data,
  loanId,
  onAction,
  working,
}: {
  data: LoanDetailResponse;
  loanId: string;
  onAction(body: Record<string, unknown>): Promise<void>;
  working: boolean;
}) {
  const currency = String(data.loan.currency_code ?? "USD");
  const paginatedPayments = usePaginatedRows(data.payments);
  const [draft, setDraft] = useState({
    additionalPrincipalAmount: "0",
    amount: "",
    idempotencyKey: crypto.randomUUID(),
    interestAmount: "",
    paymentDate: "",
    principalAmount: "",
    sageReference: "",
    schedulePeriodId: "",
  });
  const selectPeriod = (periodId: string) => {
    const period = data.periods.find((item) => String(item.id) === periodId);
    setDraft({
      additionalPrincipalAmount: "0",
      amount: String(period?.cash_payment ?? ""),
      idempotencyKey: crypto.randomUUID(),
      interestAmount: String(period?.interest_applied ?? ""),
      paymentDate: String(period?.expected_bank_payment_date ?? ""),
      principalAmount: String(period?.principal_applied ?? ""),
      sageReference: "",
      schedulePeriodId: periodId,
    });
  };
  const update = (field: keyof typeof draft, value: string) =>
    setDraft({ ...draft, [field]: value });
  const record = () => onAction({ action: "record_payment", ...draft, loanId });
  return (
    <div className="operational-stack">
      <section className="operational-section">
        <h2>Record payment</h2>
        {data.periods.length === 0 ? (
          <p className="muted-text">
            Activate an amortization schedule before recording payments.
          </p>
        ) : (
          <>
            <div className="form-grid">
              <Select
                id="payment-period"
                label="Schedule period"
                onChange={(event) => selectPeriod(event.target.value)}
                value={draft.schedulePeriodId}
              >
                <Option text="Select a due period" value="" />
                {data.periods.map((period) => (
                  <Option
                    key={String(period.id)}
                    text={`Period ${String(period.period_number)} · ${String(period.expected_bank_payment_date)}`}
                    value={String(period.id)}
                  />
                ))}
              </Select>
              <Textbox
                label="Payment date"
                onChange={(event) => update("paymentDate", event.target.value)}
                required
                value={draft.paymentDate}
              />
              <CurrencyTextbox
                currency={currency}
                label="Principal"
                onChange={(value) => update("principalAmount", value)}
                required
                value={draft.principalAmount}
              />
              <CurrencyTextbox
                currency={currency}
                label="Interest"
                onChange={(value) => update("interestAmount", value)}
                required
                value={draft.interestAmount}
              />
              <CurrencyTextbox
                currency={currency}
                label="Additional principal"
                onChange={(value) => update("additionalPrincipalAmount", value)}
                required
                value={draft.additionalPrincipalAmount}
              />
              <CurrencyTextbox
                currency={currency}
                label="Total cash"
                onChange={(value) => update("amount", value)}
                required
                value={draft.amount}
              />
              <Textbox
                label="Sage reference (optional)"
                onChange={(event) =>
                  update("sageReference", event.target.value)
                }
                value={draft.sageReference}
              />
            </div>
            <div className="button-row editor-actions">
              <Button
                disabled={
                  working ||
                  !draft.schedulePeriodId ||
                  !draft.paymentDate ||
                  !draft.amount
                }
                onClick={() => void record()}
                variant="default"
                variantType="primary"
              >
                {working ? "Recording" : "Record payment"}
              </Button>
            </div>
          </>
        )}
      </section>
      <section className="operational-section">
        <h2>Payment history</h2>
        <FlatTable
          caption="Actual payments"
          colorTheme="light"
          overflowX="auto"
        >
          <FlatTableHead>
            <FlatTableRow>
              {[
                "Period",
                "Date",
                "Scheduled",
                "Actual",
                "Principal",
                "Interest",
                "Additional",
                "Reference",
                "Status",
                "Actions",
              ].map((column) => (
                <FlatTableHeader key={column}>{column}</FlatTableHeader>
              ))}
            </FlatTableRow>
          </FlatTableHead>
          <FlatTableBody>
            {paginatedPayments.visibleRows.map((payment) => (
              <FlatTableRow key={payment.id}>
                <FlatTableCell>{payment.period_number}</FlatTableCell>
                <FlatTableCell>
                  <DateValue value={payment.payment_date} />
                </FlatTableCell>
                <FlatTableCell>
                  <FinancialAmount
                    currency={currency}
                    value={payment.scheduled_cash_payment}
                  />
                </FlatTableCell>
                <FlatTableCell>
                  <FinancialAmount currency={currency} value={payment.amount} />
                </FlatTableCell>
                <FlatTableCell>
                  <FinancialAmount
                    currency={currency}
                    value={payment.principal_amount}
                  />
                </FlatTableCell>
                <FlatTableCell>
                  <FinancialAmount
                    currency={currency}
                    value={payment.interest_amount}
                  />
                </FlatTableCell>
                <FlatTableCell>
                  <FinancialAmount
                    currency={currency}
                    value={payment.additional_principal_amount}
                  />
                </FlatTableCell>
                <FlatTableCell>{payment.sage_reference ?? "—"}</FlatTableCell>
                <FlatTableCell>
                  <StatusPill
                    label={payment.status}
                    tone={
                      payment.status === "posted"
                        ? "positive"
                        : payment.status === "voided"
                          ? "neutral"
                          : "information"
                    }
                  />
                </FlatTableCell>
                <FlatTableCell>
                  {payment.status === "confirmed" ? (
                    <Button
                      disabled={working}
                      onClick={() =>
                        void onAction({
                          action: "void_payment",
                          paymentId: payment.id,
                        })
                      }
                      variant="default"
                      variantType="tertiary"
                    >
                      Void
                    </Button>
                  ) : (
                    "—"
                  )}
                </FlatTableCell>
              </FlatTableRow>
            ))}
          </FlatTableBody>
        </FlatTable>
        {paginatedPayments.pagination}
      </section>
    </div>
  );
}
function PrincipalChart({
  periods,
}: {
  periods: LoanDetailResponse["periods"];
}) {
  if (periods.length === 0) return null;
  const values = periods.map((period) => Number(period.ending_principal));
  const maximum = Math.max(
    Number(periods[0]?.beginning_principal ?? 0),
    ...values,
    1,
  );
  const points = values
    .map(
      (value, index) =>
        `${(index / Math.max(values.length - 1, 1)) * 100},${100 - (value / maximum) * 90}`,
    )
    .join(" ");
  return (
    <figure className="principal-chart">
      <figcaption>
        <strong>Principal balance</strong>
        <span>Active schedule forecast</span>
      </figcaption>
      <svg
        aria-label="Principal balance over schedule periods"
        preserveAspectRatio="none"
        role="img"
        viewBox="0 0 100 100"
      >
        <polyline
          fill="none"
          points={points}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </figure>
  );
}
const moneyColumns = new Set([
  "amount",
  "balloon_amount",
  "estimated_value",
  "original_principal",
  "payment_amount",
]);
const dateColumns = new Set([
  "effective_date",
  "event_date",
  "first_accrual_date",
  "first_expected_bank_payment_date",
  "first_payment_application_date",
  "funding_date",
  "maturity_date",
  "origination_date",
  "valuation_date",
]);
function SimpleTable({
  caption,
  columns,
  currency = "USD",
  rows,
}: {
  caption: string;
  columns: string[];
  currency?: string;
  rows: Array<Record<string, unknown>>;
}) {
  const paginatedRows = usePaginatedRows(rows);
  const display = (row: Record<string, unknown>, column: string) => {
    const value = row[column];
    if (moneyColumns.has(column))
      return (
        <FinancialAmount
          currency={currency}
          value={value === null || value === undefined ? null : String(value)}
        />
      );
    if (column === "annual_rate")
      return (
        <RatePercentage
          value={value === null || value === undefined ? null : String(value)}
        />
      );
    if (dateColumns.has(column))
      return (
        <DateValue
          value={value === null || value === undefined ? null : String(value)}
        />
      );
    return String(value ?? "—");
  };
  return (
    <>
      <FlatTable caption={caption} colorTheme="light" overflowX="auto">
        <FlatTableHead>
          <FlatTableRow>
            {columns.map((column) => (
              <FlatTableHeader key={column}>
                {column.replaceAll("_", " ")}
              </FlatTableHeader>
            ))}
          </FlatTableRow>
        </FlatTableHead>
        <FlatTableBody>
          {paginatedRows.visibleRows.map((row, index) => (
            <FlatTableRow key={String(row.id ?? index)}>
              {columns.map((column) => (
                <FlatTableCell key={column}>
                  {display(row, column)}
                </FlatTableCell>
              ))}
            </FlatTableRow>
          ))}
        </FlatTableBody>
      </FlatTable>
      {paginatedRows.pagination}
    </>
  );
}
function Audit({ loanId }: { loanId: string }) {
  const { api } = useContext(EmbeddedSessionContext);
  const [events, setEvents] = useState<Array<Record<string, unknown>> | null>(
    null,
  );
  useEffect(() => {
    if (api)
      void getLoanView<{ events: Array<Record<string, unknown>> }>(
        api,
        "audit",
        { loanId },
      ).then((value) => setEvents(value.events));
  }, [api, loanId]);
  return events ? (
    <SimpleTable
      caption="Audit history"
      columns={[
        "created_at",
        "action",
        "record_type",
        "source",
        "correlation_id",
      ]}
      rows={events}
    />
  ) : (
    <Loader loaderLabel="Loading audit history" size="small" />
  );
}
function Metric({
  children,
  label,
}: {
  children: React.ReactNode;
  label: string;
  tone?: "default" | "positive" | "negative" | "caution";
}) {
  const icon = label.includes("principal")
    ? "card_wallet"
    : label.includes("rate")
      ? "percentage_boxed"
      : label.includes("payment") || label === "Maturity"
        ? "calendar_pay_date"
        : label === "Terms"
          ? "document_tick"
          : "analysis";
  return (
    <MetricCard icon={icon} label={label}>
      {children}
    </MetricCard>
  );
}
function expectedPaymentForDetail(data: LoanDetailResponse): string {
  const term = data.terms[0];
  const rate = data.rates[0];
  if (term?.payment_amount !== null && term?.payment_amount !== undefined)
    return String(term.payment_amount);
  try {
    return calculateExpectedPayment({
      amortizationTermMonths: Number(term?.amortization_term_months),
      annualRate: String(rate?.annual_rate),
      balloonAmount: String(term?.balloon_amount ?? "0"),
      originalPrincipal: String(term?.original_principal),
      paymentFrequency: String(term?.payment_frequency) as
        | "monthly"
        | "quarterly"
        | "annual",
      paymentTiming: String(term?.payment_timing) as "advance" | "arrears",
    });
  } catch {
    return "";
  }
}
export function selectCurrentSchedule(
  schedules: LoanDetailResponse["schedules"],
  termVersionId: string,
) {
  const current = schedules.filter(
    (item) => String(item.term_version_id) === termVersionId,
  );
  return (
    current.find((item) => item.status === "ready") ??
    current.find((item) => item.status === "active") ??
    current[0]
  );
}
